import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { TimeInMs } from '../../time-picker/types';
import { FormatService } from '../../../services/format.service';
import { IChartDataInput, TooltipInfo } from '../area-chart/types';

@Injectable({
  providedIn: 'root'
})
export class TooltipService {
  private _isDarkTheme: boolean;
  private _chartDataTitle: string;

  constructor(private formatService: FormatService) {}

  public getTooltip(
    data: IChartDataInput,
    x: number,
    points: any, //eslint-disable-line
    isDarkTheme: boolean,
    chartDataTitle: string
  ): string {
    if (!data) {
      return;
    }

    this._isDarkTheme = isDarkTheme;
    this._chartDataTitle = chartDataTitle;

    if (!data.predictedValues && !!data.criticalThresholdValues) {
      const conversations = data.conversations.find(conversation => conversation.timestamp === x);

      if (conversations && conversations.data) {
        return this._getTooltipHTML({
          data,
          measuredData: points[2],
          criticalSeries: points[1],
          warningSeries: points[0],
          conversations,
          dateTime: conversations.timestamp
        });
      }

      return this._getTooltipHTML({
        data,
        measuredData: points[2],
        criticalSeries: points[1],
        warningSeries: points[0],
        dateTime: points[2].x
      });
    }

    if (data.predictedValues) {
      const measuredDataSeries = points[1] ? points[1] : points[0]; // when predicted data is not available only 1 point is provided in the tooltip

      const conversations = data.conversations.find(conversation => conversation.timestamp === x);

      if (points[1]) {
        if (conversations.data) {
          return this._getTooltipHTML({
            data,
            measuredDataSeries,
            predictedData: points[0],
            conversations,
            dateTime: conversations.timestamp
          });
        }

        return this._getTooltipHTML({
          data,
          measuredDataSeries,
          predictedData: points[0],
          conversations,
          dateTime: conversations.timestamp
        });
      }

      if (conversations.data) {
        return this._getTooltipHTML({
          data,
          measuredDataSeries,
          conversations,
          dateTime: conversations.timestamp
        });
      }

      return this._getTooltipHTML({
        data,
        measuredDataSeries,
        conversations,
        dateTime: conversations.timestamp
      });
    }

    if (data.stackedValues) {
      return this._getTooltipHTML({
        data,
        x,
        points,
        dateTime: x
      });
    }

    return this._getTooltipHTML({
      data,
      measuredDataSeries: points[0],
      dateTime: points[0].x
    });
  }

  public getTooltipDate(dateTime: number, gapSize: number): string {
    const dateFormat = 'MMM D, YYYY H:mm';

    if (gapSize <= TimeInMs.Minute) {
      return moment(dateTime).format(dateFormat);
    }

    const gapFormat = gapSize >= TimeInMs.Day ? dateFormat : 'H:mm';

    return `${moment(dateTime).format(dateFormat)} - ${moment(dateTime)
      .add(gapSize / 1000, 'seconds')
      .format(gapFormat)}`;
  }

  private _getTooltipHTML(info: TooltipInfo) {
    // @TODO: add interface
    const {
      data,
      measuredData,
      criticalSeries,
      warningSeries,
      conversations,
      measuredDataSeries,
      predictedData,
      points,
      dateTime
    } = info;

    const head = `<div class="head">${this.getTooltipDate(dateTime, data.gapSize)}</div>`;

    const rowScore = measuredData
      ? `
      <div class="row score">
        <div class="title measured-data">${measuredData.series.name}: </div>
        <div style="color:${measuredData.color};">
        ${this.formatService.formatValueWithUnit(measuredData.y, data.datasetUnit)}</div>
      </div>
    `
      : '';
    const row = measuredDataSeries
      ? data.showHistoricalTooltip
        ? `
      <div class="row">
        <div class="title measured-data"><span class="bullet" style="color:${measuredDataSeries.series.color};">&#8226;</span> ${this._chartDataTitle}: </div>

        </div>
      </div>
    `
        : `<div class="row score">
          <div class="name measured-data"><span class="bullet" style="color:${
            measuredDataSeries.color
          };">&#8226;</span> ${this._chartDataTitle}: </div>
            ${this.formatService.formatValueWithUnit(measuredDataSeries.y, data.datasetUnit)}
          </div>
        </div> `
      : '';

    const rowsPoints = points
      ? data.showHistoricalTooltip && data.chartType !== 'dscp'
        ? points
            .map((p, index) => {
              return `
          <div class="row historical-stacked-values">
            <div class="title measured-data">
                <span class="bullet" style="color:${p.series.color}">&#8226;</span> ${p.series.name}:
            </div>
          `;
            })
            .join('</div>')
        : points
            .map(p => {
              return `
              <div class="row">
                <div class="name measured-data">
                    <span class="bullet" style="color:${p.color}">&#8226;</span> ${p.series.name}:
                </div>
                <span>${this.formatService.formatValueWithUnit(p.y, data.datasetUnit)}</span>
              </div>`;
            })
            .join('')
      : '';

    const thresholdCriticalSeries = criticalSeries
      ? `
      <div class="row">
        <div class="name"><span class="bullet" style="color:${
          criticalSeries.color
        };">&#8226;</span> ${criticalSeries.series.name}: </div>
        <span>${this.formatService.formatValueWithUnit(criticalSeries.y, data.datasetUnit)}</span>
      </div>
    `
      : '';
    const thresholdWarningSeries = warningSeries
      ? `
      <div class="row">
        <div class="name"><span class="bullet" style="color:${
          warningSeries.color
        };">&#8226;</span> ${warningSeries.series.name}: </div>
        <span>${this.formatService.formatValueWithUnit(warningSeries.y, data.datasetUnit)}</span>
      </div>
    `
      : '';
    const thresholdPredictedData = predictedData
      ? `
      <div class="row">
        <div class="name"><span class="bullet" style="color:${
          predictedData.color
        };">&#8226;</span> ${predictedData.series.name}: </div>
        ${this.formatService.formatValueWithUnit(
          predictedData.point.low,
          data.datasetUnit
        )} &lt;-&gt; ${this.formatService.formatValueWithUnit(
          predictedData.point.high,
          data.datasetUnit
        )}
      </div>
    `
      : '';

    const thresholds =
      thresholdCriticalSeries || thresholdWarningSeries || thresholdPredictedData
        ? `
      <div class="tholds">
        ${thresholdCriticalSeries}
        ${thresholdWarningSeries}
        ${thresholdPredictedData}
      </div>
    `
        : '';

    const dataContainer = `
      <div class="data">
        ${rowScore}
        ${row}
        ${rowsPoints}
        ${thresholds}
      </div>
    `;

    const clientsImpacted = conversations
      ? `
      <div class="row">
        <div class="name">Clients impacted: </div>
        <span>${conversations.data}</span>
      </div>
    `
      : '';

    return `
      <div class="tooltip-container ${this._isDarkTheme ? 'tooltip-dark' : 'tooltip-light'}">
          ${head}
          ${dataContainer}
          ${clientsImpacted}
      </div>
    `;
  }
}
